import { Link } from "@tanstack/react-router";
import { CalendarClock, GitCompareArrows, Pencil } from "lucide-react";
import { useState } from "react";
import { PersonAspirationsSection } from "@/components/persons/PersonAspirationsSection";
import { PersonRolesDialog } from "@/components/persons/PersonRolesDialog";
import {
  roleIsCurrent,
  roleIsFuture,
  roleIsPrevious,
} from "@/components/persons/person-profile/role-dates";
import type { Adorn } from "@/components/persons/person-profile/use-pending-adornments";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { usePersonAspirations } from "@/hooks/use-person-aspirations";
import { type PersonDetail, usePersonRoles } from "@/hooks/use-persons";
import { originLinkSearch } from "@/lib/detail-origin";

type RoleTab = "current" | "future" | "previous";

const TAB_LABELS: Record<RoleTab, string> = {
  current: "Current Roles",
  future: "Future Roles",
  previous: "Previous Roles",
};

const EMPTY_LABELS: Record<RoleTab, string> = {
  current: "No current roles.",
  future: "No upcoming roles.",
  previous: "No previous roles.",
};

// Format a date-only "YYYY-MM-DD" without the UTC-parse off-by-one that
// `new Date("2026-01-15")` causes in negative-offset timezones.
function formatRoleDate(value: string | null): string | null {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function roleDateRange(startDate: string | null, endDate: string | null): string | null {
  const start = formatRoleDate(startDate);
  const end = formatRoleDate(endDate);
  if (!start && !end) return null;
  return `${start ?? "—"} – ${end ?? "Present"}`;
}

/**
 * The roles panel on a person's Overview tab — current, future and previous
 * roles behind their own tabs, plus the aspirational "Working toward" block.
 *
 * Its three-way tab state is private to this panel. It used to sit at the top
 * of the Overview tab alongside everything else, where it was invisible to
 * anyone scanning the profile.
 */
export function RolesPanel({
  person,
  adorn,
  isAdmin,
}: {
  person: PersonDetail;
  adorn: Adorn;
  isAdmin: boolean;
}) {
  // `isError` matters here as much as on the Manage-roles dialog: the `= []`
  // default makes a failed fetch render as "No roles assigned yet", which is
  // what invites an admin into the dialog that then replaces the whole set.
  const { data: roles = [], isLoading, isError, refetch } = usePersonRoles(person.id);
  // Cached with the "Working toward" block below (same query key) — used to
  // seed the Compare-roles target with the aspirational role. Undefined for
  // viewers who can't see aspirations (403), so the seed is simply omitted.
  const { data: aspirationsData } = usePersonAspirations(person.id);
  const aspirationRoleId = aspirationsData?.aspirations[0]?.roleId;
  const [editing, setEditing] = useState(false);
  const [roleTab, setRoleTab] = useState<RoleTab>("current");

  const previousRoles = roles.filter((r) => roleIsPrevious(r.endDate));
  const futureRoles = roles.filter((r) => !roleIsPrevious(r.endDate) && roleIsFuture(r.startDate));
  const currentRoles = roles.filter(roleIsCurrent);
  const byTab: Record<RoleTab, typeof roles> = {
    current: currentRoles,
    future: futureRoles,
    previous: previousRoles,
  };
  // Current is always shown; Future and Previous appear only while they have a
  // role. If the active tab isn't among the visible ones (its last role moved
  // out), fall back to Current so we never show an empty phantom tab.
  const roleTabs: RoleTab[] = [
    "current",
    ...(futureRoles.length > 0 ? (["future"] as const) : []),
    ...(previousRoles.length > 0 ? (["previous"] as const) : []),
  ];
  const activeTab: RoleTab = roleTabs.includes(roleTab) ? roleTab : "current";
  const shownRoles = byTab[activeTab];
  // Seed the compare page's starting side with THIS person's primary role (not
  // the signed-in viewer's) — you're comparing from the profile you're reading.
  const comparePrimary = currentRoles.find((r) => r.isPrimary) ?? currentRoles[0];

  return (
    <DetailSection
      title="Roles"
      boldTitle
      className="gap-4 py-5"
      headerActions={
        <div className="flex items-center gap-1">
          {/* Scheduled (future-dated) role change indicator, matching every
              other effective-dated field. */}
          {adorn("roles")}
          <Button asChild variant="ghost" size="sm">
            <Link
              to="/dashboard/roles/compare"
              search={{
                ...(comparePrimary
                  ? { startRole: comparePrimary.roleId, startPerson: person.name }
                  : {}),
                // Seed the target side with the person's aspirational role so
                // "Compare roles" opens on current → working-toward.
                ...(aspirationRoleId ? { targetRole: aspirationRoleId } : {}),
              }}
            >
              <GitCompareArrows className="mr-1 size-3.5" />
              Compare roles
            </Link>
          </Button>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 size-3.5" />
              Manage
            </Button>
          )}
        </div>
      }
    >
      {isLoading ? (
        <LoadingRows />
      ) : isError ? (
        <ErrorState
          className="py-4"
          message="Couldn't load this person's roles"
          onRetry={() => {
            void refetch();
          }}
        />
      ) : roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No roles assigned yet.{" "}
          {isAdmin && (
            <button
              type="button"
              className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
              onClick={() => setEditing(true)}
            >
              Assign one
            </button>
          )}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-1 border-b">
            {roleTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setRoleTab(tab)}
                className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {TAB_LABELS[tab]} ({byTab[tab].length})
              </button>
            ))}
          </div>
          {shownRoles.length === 0 ? (
            <p className="text-sm text-muted-foreground">{EMPTY_LABELS[activeTab]}</p>
          ) : (
            <ul className="space-y-2">
              {shownRoles.map((r) => {
                // Previous shows the full range; Future shows when it starts;
                // Current stays clean (its future end date, if any, is the
                // hover-icon below).
                const subLabel =
                  activeTab === "previous"
                    ? roleDateRange(r.startDate, r.endDate)
                    : activeTab === "future" && formatRoleDate(r.startDate)
                      ? `Starts ${formatRoleDate(r.startDate)}`
                      : null;
                // A current role with a scheduled (future) end date.
                const expiresOn =
                  activeTab === "current" && r.endDate ? formatRoleDate(r.endDate) : null;
                return (
                  <li key={r.id}>
                    <Link
                      to="/dashboard/roles/$roleId"
                      params={{ roleId: r.roleId }}
                      search={originLinkSearch("person", person.id, person.name)}
                      className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">
                          {r.roleName}
                          {r.roleLevelName && (
                            <span className="text-muted-foreground"> — {r.roleLevelName}</span>
                          )}
                        </span>
                        {subLabel && (
                          <span className="text-xs text-muted-foreground">{subLabel}</span>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {expiresOn && (
                          <span
                            role="img"
                            title={`Expires ${expiresOn}`}
                            aria-label={`Expires ${expiresOn}`}
                            className="text-muted-foreground"
                          >
                            <CalendarClock className="size-3.5" />
                          </span>
                        )}
                        {r.isPrimary && activeTab === "current" && (
                          <StatusPill tone="success" label="Primary" />
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {/* Single aspirational role — "Working toward", below the role tabs in
          the same panel. Self-hides for viewers not allowed to see it. */}
      <PersonAspirationsSection
        personId={person.id}
        personName={person.name}
        currentRoleIds={roles.map((r) => r.roleId)}
      />
      {isAdmin && (
        <PersonRolesDialog
          open={editing}
          onOpenChange={setEditing}
          personId={person.id}
          personName={person.name}
        />
      )}
    </DetailSection>
  );
}
